// Navigation menu configuration
// Defines the structure of the navigation menu items, including dropdown sections and links
import type { JSX } from "solid-js";

export interface NavLink {
  label: string;
  href: string;
  icon?: JSX.Element;
  description?: string;
  isComingSoon?: boolean;
}

export interface NavSection {
  title: string;
  subtitle?: string;
  links: NavLink[];
}

export interface DropdownConfig {
  sections: NavSection[];
}

export interface SimpleDropdownConfig {
  links: NavLink[];
}

// Platform dropdown configuration
export const platformConfig: DropdownConfig = {
  sections: [
    {
      title: "EXPLORE",
      subtitle: "Turn unstructured data into clarity",
      links: [
        {
          label: "Turn Data Chaos to Action",
          description: "Go from cross-source data to decisive action.",
          href: "https://web.storytell.ai/every-file-meaningful-insights",
        },
        {
          label: "Context-Aware Intelligence",
          description: "AI that understands your org's language and context.",
          href: "https://web.storytell.ai/gain-contextual-intelligence",
        },
        {
          label: "Dynamic Insights",
          description: "Surface patterns, trends, and hidden signals automatically",
          href: "https://web.storytell.ai/dynamic-insights",
        },
        {
          label: "Trust but Verify",
          description: "Validate every answer with sources and evidence",
          href: "https://web.storytell.ai/trust-but-verify",
        },
      ],
    },
    {
      title: "ACT",
      subtitle: "Turn insight into action—fast",
      links: [
        {
          label: "Get Better Answers",
          description: "Refine prompts, ask follow-ups and dig deeper all in one-click",
          href: "https://web.storytell.ai/get-better-answers",
        },
        {
          label: "Dynamic LLM Router",
          description: "Get the perfect answer, every time",
          href: "https://web.storytell.ai/llm-router",
        },
        {
          label: "Actions",
          description: "Automate next steps from live insights—alerts, actions, follow-ups.",
          href: "https://web.storytell.ai/actions",
          isComingSoon: true,
        },
        {
          label: "Personalized AI",
          description: "Tailor Storytell to work like a teammate.",
          href: "https://web.storytell.ai/personalized-ai",
          isComingSoon: true,
        },
      ],
    },
    {
      title: "COLLABORATE",
      subtitle: "Work smarter together with shared intelligence",
      links: [
        {
          label: "Collections",
          description: "Save, organize, and share insights across teams",
          href: "https://web.storytell.ai/create-team-project-spaces",
        },
        {
          label: "De-Silo Teams",
          description: "Connect files, chats, and context across the org",
          href: "https://web.storytell.ai/de-silo-teams",
        },
      ],
    },
    {
      title: "SECURITY & CONTROL",
      subtitle: "Control how Storytell works for your org",
      links: [
        {
          label: "Trust & Security",
          description: "Stay compliant with enterprise-grade governance",
          href: "https://web.storytell.ai/trust-and-security",
        },
        {
          label: "Cost Management",
          description: "Track & optimize AI usage by team & use case",
          href: "https://web.storytell.ai/manage-costs",
        },
        {
          label: "Enterprise Controls",
          description: "Manage teams, access levels, and data flow",
          href: "https://web.storytell.ai/enterprise-controls",
          isComingSoon: true,
        },
      ],
    },
  ],
};

// Resources dropdown configuration
export const resourcesConfig: SimpleDropdownConfig = {
  links: [
    {
      label: "Documentation",
      href: "https://docs.storytell.ai/introduction",
    },
    {
      label: "Blog",
      href: "https://web.storytell.ai/blog",
    },
    {
      label: "Webinars & Workshops",
      href: "https://web.storytell.ai/webinars",
    },
    {
      label: "AI Prompt Library",
      href: "https://web.storytell.ai/prompts",
    },
  ],
};

// Static links configuration
export const staticLinks = [
  {
    label: "Why Storytell",
    href: "https://web.storytell.ai/why-storytell-enterprise-ai",
  },
  {
    label: "Book an Enterprise Demo",
    href: "https://web.storytell.ai/book-a-demo",
  },
];

// Detailed Use Cases configuration for desktop dropdown
export const detailedUseCasesConfig: NavSection[] = [
  {
    title: "Product",
    links: [
      {
        href: "https://web.storytell.ai/use-case/create-product-requirement-docs",
        label: "Create Product Requirement Docs",
      },
      { href: "https://web.storytell.ai/use-case/sprint-planning", label: "Sprint Planning" },
      {
        href: "https://web.storytell.ai/use-case/prioritization-roadmap-insights",
        label: "Prioritization & Roadmap Insights",
      },
    ],
  },
  {
    title: "Venture Capital",
    links: [
      {
        href: "https://web.storytell.ai/use-case/financial-risk-valuation-analysis",
        label: "Financial Risk & Valuation Analysis",
      },
      { href: "https://web.storytell.ai/use-case/company-deep-dives", label: "Company Deep Dives" },
      { href: "https://web.storytell.ai/use-case/analyze-startup-pitch-deck", label: "Pitch Deck Analysis" },
    ],
  },
  {
    title: "Analyst",
    links: [
      { href: "https://web.storytell.ai/use-case/investor-updates", label: "Investor Updates" },
      { href: "https://web.storytell.ai/use-case/market-landscape-analysis", label: "Market Landscape Analysis" },
    ],
  },
  {
    title: "Finance",
    links: [{ href: "https://web.storytell.ai/use-case/revenue-forecast-reports", label: "Revenue Forecast Reports" }],
  },
  {
    title: "Sales",
    links: [{ href: "https://web.storytell.ai/use-case/personalized-sales-pitch", label: "Personalized Sales Pitch" }],
  },
  {
    title: "Marketing",
    links: [{ href: "https://web.storytell.ai/use-case/sales-battlecards", label: "Sales Battlecards" }],
  },
  {
    title: "Customer Success",
    links: [
      {
        href: "https://web.storytell.ai/use-case/churn-risk-prediction-retention-strategy",
        label: "Churn Risk Prediction & Retention Strategy",
      },
    ],
  },
  {
    title: "Team Dynamics",
    links: [
      {
        href: "https://web.storytell.ai/use-case/master-interpersonal-interactions",
        label: "Improve team communications",
      },
    ],
  },
  {
    title: "Security & Compliance",
    links: [
      {
        href: "https://web.storytell.ai/use-case/security-data-into-documentation",
        label: "Create security documentation",
      },
    ],
  },
];
